import type { AdminResourceItemDef } from "./resource-catalog.types";

export const ADMIN_RESOURCE_CATALOG_ADMIN: readonly AdminResourceItemDef[] = [
  // ── Admin ───────────────────────────────────────────────────────────────────
  {
    id: "platform-operator-keys",
    name: "Platform Operator Keys",
    description:
      "Cross-tenant API keys (nxs_o_) for platform engineers — one credential that acts on any organization, without an OrganizationMembership row. Minting is ROOT-only; every use is written to the admin audit log. See NEX-3037.",
    category: "Admin",
    // NOTE: there is deliberately NO `mint` action here. Minting is
    // `@GodmodeOnly()` and the endpoint never consults a PBAC permission, so
    // listing it would let the policy picker grant an access that does not
    // exist — a promise the system cannot keep, and the failure mode `navOnly`
    // above exists to avoid. Ungrantable by construction is the intent; the
    // catalog must not quietly say otherwise.
    actions: [
      {
        id: "view",
        name: "View Operator Keys",
        description: "List active platform-operator keys, their owner, scopes and expiry"
      },
      {
        id: "revoke",
        name: "Revoke Operator Key",
        description: "Immediately revoke a platform-operator key"
      }
    ]
  },
  {
    id: "deletion-policy",
    name: "Deletion Policy",
    description:
      "System-wide toggle controlling whether Agent and Deployment deletes are SOFT (reversible via restore) or HARD (tombstone snapshot, irreversible).",
    category: "Admin",
    actions: [
      {
        id: "view",
        name: "View Policy",
        description: "View the current deletion policy for Agents and Deployments"
      },
      {
        id: "update",
        name: "Update Policy",
        description: "Change SOFT|HARD mode for Agents and/or Deployments"
      }
    ]
  },
  {
    id: "migrations",
    name: "Migrations",
    description: "Database schema and data migrations",
    category: "Admin",
    blocks: [
      {
        id: "database",
        name: "Database Migrations",
        description: "Prisma schema migrations",
        unserved:
          "Nothing reads this block or either action below. Prisma migrations are served under " +
          "the top-level `admin:database` item, whose `view-migrations` and `deploy-migrations` " +
          "actions carry the real guards — this block is a second name for the same capability.",
        actions: [
          { id: "view", name: "View Migrations", description: "View migration history" },
          { id: "deploy", name: "Deploy Migrations", description: "Run pending migrations" }
        ]
      },
      {
        id: "webhooks",
        name: "Webhook Migration",
        description: "Migrate Twilio webhook URLs",
        unserved:
          "Nothing reads this block or the action below. The webhook migration route guards the " +
          "item root `admin:migrations`; only `attribution` is addressed at its own path.",
        actions: [{ id: "migrate", name: "Migrate Webhooks", description: "Update webhook URLs" }]
      },
      {
        id: "oauth-metadata",
        name: "OAuth Metadata",
        description: "Backfill OAuth connection metadata",
        unserved:
          "Nothing reads this block or the action below. The backfill route guards the item " +
          "root `admin:migrations`.",
        actions: [
          { id: "backfill", name: "Backfill Metadata", description: "Update OAuth metadata" }
        ]
      },
      {
        id: "attribution",
        name: "Deployment Attribution",
        description:
          "Backfill deploymentId on the analytics fact tables for rows written before the columns existed",
        actions: [
          {
            id: "run",
            name: "Run Attribution Backfill",
            description: "Sweep Chat, Message, WorkflowExecution, WorkflowExecutionNode and Score"
          }
        ]
      },
      {
        id: "credentials-encryption",
        name: "Credentials Encryption",
        description: "Encrypt ApiKeyConnection credentials (Twilio, Telegram, Slack, Teams)",
        unserved:
          "Nothing reads this block or either action below. Credential encryption is served " +
          "under the top-level `admin:credentials-encryption` item, which carries the real " +
          "guards — this block is a second name for the same capability.",
        actions: [
          {
            id: "encrypt-all",
            name: "Encrypt All",
            description: "Encrypt plaintext credentials with active key"
          },
          {
            id: "rotate-key",
            name: "Rotate to New Key",
            description: "Re-encrypt all credentials with new encryption key"
          }
        ]
      }
    ]
  },
  {
    id: "system-config",
    name: "System Configuration",
    description:
      "Operational config management (cron schedules, model catalog, rate limits, circuit breakers).",
    category: "Admin",
    actions: [
      { id: "read", name: "View Configs", description: "View system configurations" },
      { id: "write", name: "Edit Configs", description: "Modify operational settings" }
    ]
  },
  {
    id: "agent",
    name: "Admin Agent",
    description: "Internal admin AI agent console",
    category: "Admin"
  },
  {
    id: "policies",
    name: "Permission Policies",
    description: "Create and manage admin PBAC policies and their rules",
    category: "Admin",
    actions: [
      { id: "create", name: "Create Policies", description: "Create new policies" },
      { id: "edit", name: "Edit Policies", description: "Modify policy rules" },
      { id: "delete", name: "Delete Policies", description: "Remove policies" }
    ]
  },
  {
    id: "admins",
    name: "Admin Users",
    description: "Manage who can access the admin panel and their assigned policy",
    category: "Admin",
    actions: [
      { id: "create", name: "Add Admin Users", description: "Grant admin-panel access" },
      { id: "edit", name: "Edit Admin Users", description: "Reassign policy or overrides" },
      { id: "delete", name: "Remove Admin Users", description: "Revoke admin-panel access" }
    ]
  },
  {
    id: "audit",
    name: "Audit Log",
    description: "Admin-panel activity log",
    category: "Admin",
    actions: [
      { id: "view", name: "View Logs", description: "View activity history" },
      { id: "export", name: "Export Logs", description: "Export activity to CSV" }
    ]
  }
] as const;
